var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * react-query hooks for the bulk revalidate job (TASK-159 / AV-01).
 *
 * Two surfaces:
 *  - `useStartRevalidate()` — mutation that kicks off a job (`POST /revalidate`).
 *  - `useRevalidateJobStatus(jobId)` — polling query (`GET /revalidate/{jobId}`)
 *     that refetches every 2 s while the job is still active, then stops
 *     polling once the state is terminal (COMPLETED / FAILED / CANCELLED).
 *
 * Closure-captures `ky` (MEMORY: useOkapiKy returns an unstable instance).
 *
 * The polling cadence (2 s) matches the API contract from the plan and the
 * UC AV-01 acceptance criteria — fast enough that progress bars feel live,
 * slow enough to avoid hammering the backend with 30k+ records to scan.
 */
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { revalidate, getJobStatus, getCurrentJob } from '../api/validationService';
import { TERMINAL_JOB_STATES } from '../types/validation';
var POLL_INTERVAL_MS = 2000;
var IDLE_POLL_INTERVAL_MS = 8000;
/** Query key of {@link useCurrentRevalidateJob} — shared with the optimistic write below. */
export var CURRENT_REVALIDATE_JOB_KEY = ['validation-revalidate-current'];
/**
 * CZNA-1620 — the started job as `GET /revalidate/current` will report it, built from the
 * ack: QUEUED, nothing processed yet. The scope is the ack's, falling back to the one asked
 * for, so a CORPUS_CHECKS start still reads as the checks' job.
 */
var queuedFromAck = function (ack, req) {
    var _a;
    return ({
        jobId: ack.jobId,
        state: 'QUEUED',
        scope: (_a = ack.scope) !== null && _a !== void 0 ? _a : req.scope,
        processed: 0,
        total: 0,
        errorsFound: 0,
        warningsFound: 0,
        startedAt: ack.submittedAt,
        finishedAt: null,
        errorMessage: null,
    });
};
/**
 * Starts a job. On success the started job goes straight into the current-job cache as
 * QUEUED and that query is re-read (CZNA-1620): until then the cache still says "idle" — up
 * to the idle poll interval — and every caller would offer its run button again, whose second
 * click the backend refuses with 409. The poll that follows replaces the optimistic entry
 * with the real state, or with null when the job has already finished.
 */
export var useStartRevalidate = function (options) {
    if (options === void 0) { options = {}; }
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (req) { return revalidate(ky, req); }, {
        onSuccess: function (ack, req) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: 
                    // A poll already in flight was asked before the start and would answer "idle" over
                    // the optimistic entry; drop it.
                    return [4 /*yield*/, queryClient.cancelQueries(CURRENT_REVALIDATE_JOB_KEY)];
                    case 1:
                        // A poll already in flight was asked before the start and would answer "idle" over
                        // the optimistic entry; drop it.
                        _b.sent();
                        queryClient.setQueryData(CURRENT_REVALIDATE_JOB_KEY, queuedFromAck(ack, req));
                        queryClient.invalidateQueries(CURRENT_REVALIDATE_JOB_KEY);
                        (_a = options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, ack);
                        return [2 /*return*/];
                }
            });
        }); },
        onError: options.onError,
    });
};
export var useRevalidateJobStatus = function (jobId, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var ky = useOkapiKy();
    var interval = (_a = options.refetchInterval) !== null && _a !== void 0 ? _a : POLL_INTERVAL_MS;
    return useQuery(['validation-revalidate-job', jobId], function () { return getJobStatus(ky, jobId); }, {
        enabled: !!jobId && options.enabled !== false,
        refetchInterval: function (data) {
            if (!data)
                return interval;
            return TERMINAL_JOB_STATES.has(data.state) ? false : interval;
        },
        // Once the job finishes we still want the final payload visible, but
        // we don't want react-query to silently refetch and overwrite it with
        // a 404 if the backend GCs the job.
        refetchOnWindowFocus: false,
    });
};
/**
 * Combined mutation+polling surface for callers that want a one-shot
 * "kick + watch" interaction (e.g. the ValidationPanel "Revalidate all"
 * button). Returns the start mutation, the latest job id, and the status
 * query in a single object.
 */
export var useRevalidateJob = function () {
    var _a, _b;
    var start = useStartRevalidate();
    var jobId = (_b = (_a = start.data) === null || _a === void 0 ? void 0 : _a.jobId) !== null && _b !== void 0 ? _b : null;
    var status = useRevalidateJobStatus(jobId);
    return {
        start: start.mutate,
        startAsync: start.mutateAsync,
        isStarting: start.isLoading,
        startError: start.error,
        jobId: jobId,
        status: status.data,
        isPolling: status.isFetching,
        statusError: status.error,
        reset: start.reset,
    };
};
export default useRevalidateJob;
/**
 * Polls `GET /revalidate/current` to discover whether a bulk revalidation
 * job is currently running.
 *
 * Resolves to `null` while idle (HTTP 204 from the backend) and to a
 * {@link RevalidateJobStatus} while a job is in flight. Cadence adapts:
 *
 *  - **Idle:** poll every {@link IDLE_POLL_INTERVAL_MS} (8 s) so the banner
 *    appears reasonably fast if another user starts a job from a different
 *    session, without hammering the endpoint on a quiet page.
 *  - **Active:** poll every {@link POLL_INTERVAL_MS} (2 s) so the progress
 *    counter feels live.
 *
 * Once the job reaches a terminal state we keep refetching at the idle
 * cadence — that way the banner can show the final summary briefly and the
 * UI naturally returns to "no active job" on the next tick.
 */
export var useCurrentRevalidateJob = function (options) {
    var _a, _b;
    if (options === void 0) { options = {}; }
    var ky = useOkapiKy();
    var activeInterval = (_a = options.activePollInterval) !== null && _a !== void 0 ? _a : POLL_INTERVAL_MS;
    var idleInterval = (_b = options.idlePollInterval) !== null && _b !== void 0 ? _b : IDLE_POLL_INTERVAL_MS;
    return useQuery(CURRENT_REVALIDATE_JOB_KEY, function () { return getCurrentJob(ky); }, {
        enabled: options.enabled !== false,
        refetchInterval: function (data) {
            if (!data)
                return idleInterval;
            if (TERMINAL_JOB_STATES.has(data.state))
                return idleInterval;
            return activeInterval;
        },
        refetchOnWindowFocus: false,
    });
};
